import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { DollarSign, ShoppingCart, Users, Package, Store, UserCheck, TrendingUp, ArrowUpRight } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [fastMovement, setFastMovement] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, chartRes, fmRes] = await Promise.all([
        api.dashboardStats(),
        api.salesChart(),
        api.fastMovement()
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
      setFastMovement(fmRes.data);
    } catch (err) {
      console.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short' });
  };

  const statCards = [
    { key: 'total_sales_today', value: formatCurrency(stats?.total_sales_today), icon: DollarSign, accent: 'stat-accent-orange' },
    { key: 'total_orders_today', value: stats?.total_orders_today || 0, icon: ShoppingCart, accent: 'stat-accent-green' },
    { key: 'total_products', value: stats?.total_products || 0, icon: Package, accent: 'stat-accent-blue' },
    { key: 'total_customers', value: stats?.total_customers || 0, icon: Users, accent: 'stat-accent-purple' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-3 border-[#EB7B44] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>
          {t('welcome')}, {user?.name}
        </h1>
        <p className="text-[#64584F] mt-1">{t('sales_overview')}</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children" data-testid="stat-cards">
        {statCards.map((card) => (
          <Card key={card.key} className={`bg-white border-[#CACACC] rounded-xl shadow-sm hover:shadow-md transition-shadow ${card.accent} animate-fade-in`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-[#64584F] uppercase tracking-wider mb-1">{t(card.key)}</p>
                  <p className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }} data-testid={`stat-${card.key}`}>
                    {card.value}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#F6F5F5] flex items-center justify-center">
                  <card.icon size={20} className="text-[#EB7B44]" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sales Chart */}
        <Card className="lg:col-span-8 bg-white border-[#CACACC] rounded-xl shadow-sm" data-testid="sales-chart-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#202020]" style={{ fontFamily: 'Manrope' }}>
                {t('daily_sales')}
              </CardTitle>
              <TrendingUp size={18} className="text-[#EB7B44]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EB7B44" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#EB7B44" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DFDEDE" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 12, fill: '#64584F' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#64584F' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: '1px solid #CACACC', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    formatter={(value) => [formatCurrency(value), 'Sales']}
                    labelFormatter={formatDate}
                  />
                  <Area type="monotone" dataKey="sales" stroke="#EB7B44" strokeWidth={2.5} fill="url(#salesGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Fast Movement Products */}
        <Card className="lg:col-span-4 bg-white border-[#CACACC] rounded-xl shadow-sm" data-testid="fast-movement-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#202020]" style={{ fontFamily: 'Manrope' }}>
                {t('fast_movement')}
              </CardTitle>
              <ArrowUpRight size={18} className="text-[#10B981]" />
            </div>
          </CardHeader>
          <CardContent>
            {fastMovement.length === 0 ? (
              <p className="text-sm text-[#A6A2A0] text-center py-8">{t('no_data')}</p>
            ) : (
              <div className="space-y-3">
                {fastMovement.slice(0, 6).map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#DFDEDE] last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 rounded-full bg-[#EB7B44]/10 text-[#EB7B44] flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-sm text-[#202020] truncate">{item.product_name}</span>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-sm font-semibold text-[#202020]">{item.total_sold} sold</p>
                      <p className="text-xs text-[#A6A2A0]">{formatCurrency(item.total_revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom row - Store and Employee stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#EB7B44]/10 flex items-center justify-center">
              <Store size={24} className="text-[#EB7B44]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs font-medium text-[#64584F] uppercase tracking-wider">{t('total_stores')}</p>
              <p className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{stats?.total_stores || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
              <UserCheck size={24} className="text-[#3B82F6]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs font-medium text-[#64584F] uppercase tracking-wider">{t('total_employees')}</p>
              <p className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>{stats?.total_employees || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
